import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatAmount } from '@/lib/utils'
import type { DailyPacePoint, MonthlyStatistics } from '@/lib/statistics'

type Props = {
  pace: DailyPacePoint[]
  stats: MonthlyStatistics
  baselineTotal: number
}

type TooltipPayloadEntry = {
  name?: string
  value?: number | string | null
  color?: string
  dataKey?: string
  payload?: DailyPacePoint
}

function PaceTooltipContent({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: TooltipPayloadEntry[]
  label?: string | number
}) {
  if (!active || !payload || payload.length === 0) return null
  const point = payload[0].payload as DailyPacePoint | undefined
  if (!point) return null
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <div className="text-muted-foreground">{label}日</div>
      <div className="mt-1 space-y-0.5 tabular-nums">
        {point.current !== null && (
          <div className="flex items-center justify-between gap-3">
            <span>今月</span>
            <span className="font-medium">{formatAmount(point.current)}</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 text-muted-foreground">
          <span>平均</span>
          <span>{formatAmount(point.baseline)}</span>
        </div>
      </div>
    </div>
  )
}

export function PaceCard({ pace, stats, baselineTotal }: Props) {
  if (pace.length === 0) return null

  // "Today" in the pace array — the latest day that has current-month data.
  // For an in-progress month this is `daysElapsed`; for a past month it's the
  // last day; for a future month it's null and the pace note won't render.
  const lastVisibleDay =
    stats.daysElapsed > 0 ? (pace[stats.daysElapsed - 1] ?? null) : pace[pace.length - 1]
  const finalProjected = pace[pace.length - 1].projected
  const projectedVsBaseline =
    finalProjected !== null && baselineTotal > 0
      ? ((finalProjected - baselineTotal) / baselineTotal) * 100
      : null

  const fmtTick = (value: number) =>
    value >= 10000 ? `${Math.round(value / 10000)}万` : value.toLocaleString()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">支出ペース</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={pace} margin={{ top: 5, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="paceCurrentFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                type="number"
                domain={[1, pace.length]}
                ticks={[1, 10, 20, pace.length]}
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={fmtTick}
                width={42}
              />
              <Tooltip content={<PaceTooltipContent />} cursor={{ stroke: 'currentColor', strokeOpacity: 0.15 }} />
              <Line
                type="monotone"
                dataKey="baseline"
                stroke="currentColor"
                strokeOpacity={0.35}
                strokeDasharray="4 3"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="current"
                stroke="var(--primary)"
                strokeWidth={2}
                fill="url(#paceCurrentFill)"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              {stats.daysElapsed > 0 && stats.daysElapsed < pace.length && (
                <ReferenceLine
                  x={stats.daysElapsed}
                  stroke="currentColor"
                  strokeOpacity={0.2}
                  strokeDasharray="2 2"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-y-1 text-xs tabular-nums">
          <div className="flex items-center gap-3">
            <Legend swatchClass="bg-primary" label="今月の累計" />
            <Legend swatchClass="bg-muted-foreground/40" label="6ヶ月平均" dashed />
          </div>
          {finalProjected !== null && (
            <div className="text-muted-foreground">
              月末予測:{' '}
              <span className="font-medium text-foreground">
                {formatAmount(finalProjected, 'JPY', true)}
              </span>
              {projectedVsBaseline !== null && (
                <span className="ml-1">
                  ({projectedVsBaseline >= 0 ? '+' : ''}
                  {Math.round(projectedVsBaseline)}%)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Inline note when far ahead/behind baseline */}
        {lastVisibleDay && lastVisibleDay.current !== null && lastVisibleDay.baseline > 0 && (
          <PaceNote current={lastVisibleDay.current} baseline={lastVisibleDay.baseline} />
        )}
      </CardContent>
    </Card>
  )
}

function PaceNote({ current, baseline }: { current: number; baseline: number }) {
  const ratio = current / baseline
  if (ratio > 1.15) {
    return (
      <p className="mt-2 text-xs text-destructive">
        現時点で平年より +{Math.round((ratio - 1) * 100)}% のペース
      </p>
    )
  }
  if (ratio < 0.85) {
    return (
      <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-500">
        現時点で平年より {Math.round((ratio - 1) * 100)}% のペース
      </p>
    )
  }
  return null
}

function Legend({ swatchClass, label, dashed }: { swatchClass: string; label: string; dashed?: boolean }) {
  return (
    <span className="flex items-center gap-1 text-muted-foreground">
      <span
        className={`inline-block h-0.5 w-4 ${dashed ? '' : swatchClass}`}
        style={
          dashed
            ? {
                backgroundImage:
                  'repeating-linear-gradient(to right, currentColor 0 4px, transparent 4px 7px)',
              }
            : undefined
        }
      />
      {label}
    </span>
  )
}
